import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import FormFieldWrapper from '../../../../../common/components/FormFieldWrapper/FormFieldWrapper';
import TextInput from '../../../../../common/components/TextInput';
import { useUsersCRUDActions } from '@mm/state/actions/UsersCRUDActions';
import {
  selectCurrentUser,
  selectSessionSelector,
} from '@mm/state/selectors/userSelector';
import { activeCompanySelector } from '@mm/state/selectors/viewSelectors';
import './ProfilePaneSection.pcss';
import { EMAIL_REGEX } from '../../../../../common/constants/validations';
import CompanyUsersModel from '@mm/state/model/CompanyUsersModel';
import { UserRoleSelector } from '../../../../../common/components/UserRoleSelector/UserRoleSelector';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import classNames from 'classnames';

export const ProfilePaneSection = ({
  user,
  setUser,
  editableEmail,
  editableRole,
  css,
}: {
  user?: CompanyUsersModel;
  setUser?: (newUser: CompanyUsersModel) => void;
  editableEmail?: boolean;
  editableRole?: boolean;
  css?: { container?: string };
}) => {
  const company = useSelector(activeCompanySelector);
  const session = useSelector(selectSessionSelector);
  const currentActiveUser = useSelector(selectCurrentUser);
  const currentUser = user || currentActiveUser;
  const [email, setEmail] = useState<string | undefined>();
  const [role, setRole] = useState(user?.role);
  const hasAllRolesFlag = !!useSelector(combinedFeatureFlagsSelector)
    .userInvitesEditorAndAdmin;

  const { retrieveById, update } = useUsersCRUDActions();

  useEffect(() => {
    setEmail(m => m ?? currentUser?.email);
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser && company) {
      retrieveById(company.id, session.id);
    }
  }, [currentUser, company]);

  const saveEmail = () => {
    if (company && currentUser && email) {
      update(company.id, { ...currentUser, email });
    }
  };
  const emailDirty = currentUser && email !== currentUser.email;
  const valid = email && EMAIL_REGEX.test(email);

  const className = 'profile-pane-section__field-wrapper';
  const disabledStyle = {
    backgroundColor: '#fafafa',
    color: '#8c8c8c',
    cursor: 'not-allowed',
  };

  if (!currentUser) {
    return null;
  }

  return (
    <div className={classNames('profile-pane-section__fields', css?.container)}>
      <div className="profile-pane-section__names">
        <FormFieldWrapper label="First Name" className={className}>
          <TextInput
            disabled
            style={disabledStyle}
            value={currentUser?.firstName}
          />
        </FormFieldWrapper>
        <FormFieldWrapper label="Last Name" className={className}>
          <TextInput
            disabled
            style={disabledStyle}
            value={currentUser?.lastName}
          />
        </FormFieldWrapper>
      </div>
      <FormFieldWrapper label="Email" className={className}>
        <TextInput
          value={email || ''}
          disabled={!editableEmail}
          style={!editableEmail ? disabledStyle : undefined}
          onChange={e => {
            setEmail(e.target.value);
            setUser?.({ ...currentUser, email: e.target.value });
          }}
          error={email !== undefined && !valid}
        />
      </FormFieldWrapper>
      {editableRole && user ? (
        <div className="profile-pane-section__input-with-header">
          <div className="profile-pane-section__header">Role</div>
          {role && (
            <UserRoleSelector
              value={role}
              onUpdate={value => {
                setRole(value);
                setUser?.({
                  ...currentUser,
                  role: value,
                });
              }}
              showTeamBagde={!hasAllRolesFlag}
              hideCompanyRoles={!hasAllRolesFlag}
            />
          )}
        </div>
      ) : (
        <FormFieldWrapper label="Role" className={className}>
          <TextInput disabled style={disabledStyle} value={currentUser?.role} />
        </FormFieldWrapper>
      )}
      {emailDirty && (
        <PrimaryButton
          className="profile-pane-section__save-button"
          disabled={!valid}
          onClick={saveEmail}
        >
          Save
        </PrimaryButton>
      )}
    </div>
  );
};
